import { useState, useCallback } from "react";
import axios from "axios";

/**
 * @template T
 * @template E
 * @typedef {Object} ResApi
 * @property {T|null} data
 * @property {E|string|null} error
 * @property {boolean} isError
 */

/**
 * @template T
 * @template E
 * @typedef {Object} UseFetchReturn
 * @property {T|null} data
 * @property {boolean} loading
 * @property {E|string|null} error
 * @property {(configOverride?: import("axios").AxiosRequestConfig, isReattempt?: boolean) => Promise<ResApi<T,E>>} fetchData
 */

/**
 * Custom React hook to fetch data with Axios.
 *
 * @template T
 * @template E
 * @param {string} url - API endpoint to call.
 * @param {import("axios").AxiosRequestConfig} [options] - Optional Axios configuration.
 * @returns {UseFetchReturn<T,E>}
 */
const useFetch = (url, options) => {
  /** @type {[T|null, Function]} */
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  /** @type {[E|string|null, Function]} */
  const [error, setError] = useState(null);

  const fetchData = useCallback(
    /**
     * @param {import("axios").AxiosRequestConfig} [configOverride]
     * @param {boolean} [isReattempt]
     * @returns {Promise<ResApi<T,E>>}
     */
    async (configOverride, isReattempt) => {
      setLoading(true);
      setError(null);

      /** @type {import("axios").AxiosRequestConfig} */
      const axiosConfig = {
        ...configOverride,
        withCredentials:true,
        
      };

      try {
        const response = await axios({
          url: `${process.env.REACT_APP_BACKEND}${url}`,
          ...options,
          ...axiosConfig,
        });

        setData(response.data);
        return { data: response.data, error: null, isError: false };
      } catch (err) {
        let errorMsg = /** @type {E|string} */ ("An unknown error occurred");
        setData(null);

        if (axios.isAxiosError(err) && err.response) {
          errorMsg =
            /** @type {E} */ (err.response.data) ||
            "An error occurred while fetching data";
        } else if (err instanceof Error) {
          errorMsg = err.message;
        }

        setError(errorMsg);
        return { data: null, error: errorMsg, isError: true };
      } finally {
        setLoading(false);
      }
    },
    [url, options]
  );

  return { data, loading, error, fetchData };
};

export default useFetch;
